import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import { CardActionArea } from '@prisjakt/ui/card';
import {
  TitleMediumText,
  BodySmallText,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import Clickable from '@prisjakt/ui-clickable';
import { CompareCheckbox } from '../compare-checkbox';
import { DealBadge } from '../deal-badge';
import { ProductMedia } from '../media';
import { Price } from '../price';

import { BaseProps } from '../../types';

import * as S from './styles';

type Props = Omit<
  BaseProps,
  'id' | 'priceInclShipping' | 'priceCount' | 'addAlertButton'
>;

export const ProductGridCard = ({
  actionButton,
  brand,
  category,
  className,
  diffPercentage,
  highlightedName,
  imageSrc,
  imageSize = 140,
  lazyLoadImage = false,
  link,
  name,
  onClick,
  onDeselect = () => {},
  onSelect = () => {},
  price,
  rank,
  rating,
  reviewsCount,
  selectable,
  selected = false,
  properties,
}: Props) => {
  const { t, renderMode } = useContext(AppContext);

  const handleSelect = () => {
    if (selected) {
      onDeselect();
    } else {
      onSelect();
    }
  };

  const coreProperties = Array.isArray(properties)
    ? properties
        .filter(({ isCore, pretty }) => isCore && pretty !== null)
        .map(({ pretty }) => pretty)
        .join(', ')
    : null;

  return (
    <S.Card
      className={className}
      outlined
      selected={selected}
      containerProps={{ 'data-test': 'ProductGridCard' }}
    >
      {actionButton && <S.AddToList>{actionButton}</S.AddToList>}

      <CardActionArea
        container={Clickable}
        containerProps={{
          to: selectable ? undefined : link,
          'aria-label': name,
          isExternal: renderMode === 'native',
          onClick: selectable ? handleSelect : onClick,
          style: { flex: '1 1 auto', cursor: 'pointer' },
        }}
      >
        {selectable ? <CompareCheckbox selected={selected} /> : null}

        <S.Media $size={lazyLoadImage ? `${imageSize}px` : 'auto'}>
          {brand?.logo && <S.BrandLogo src={brand.logo} alt={brand.name} />}

          {diffPercentage ? (
            <DealBadge diffPercentage={diffPercentage} />
          ) : null}

          <ProductMedia
            lazyLoadImage={lazyLoadImage}
            imageSize={imageSize}
            imageSrc={imageSrc}
            name={name || ''}
          />
        </S.Media>

        <S.Content>
          <TitleMediumText containerProps={{ 'data-test': 'ProductName' }}>
            {highlightedName || name}
          </TitleMediumText>
          {category?.name && !rank && (
            <BodySmallText emphasis={FontEmphasis.MEDIUM}>
              {category.name}
            </BodySmallText>
          )}
          {typeof coreProperties === 'string' && (
            <S.Properties>
              <BodySmallText emphasis={FontEmphasis.MEDIUM}>
                {coreProperties}
              </BodySmallText>
            </S.Properties>
          )}

          {rating && rating > 0 ? (
            <S.Rating
              rate={rating}
              count={reviewsCount}
              showCounterAs="number"
            />
          ) : null}

          {price === undefined || price === null ? null : (
            <S.PriceWrapper gap={0.25}>
              <BodySmallText emphasis={FontEmphasis.MEDIUM}>
                {t('product', 'price.from-abbr')}
              </BodySmallText>

              <Price price={price} isDeal={Boolean(diffPercentage)} />
            </S.PriceWrapper>
          )}
        </S.Content>
      </CardActionArea>

      {rank && category ? (
        <S.Footer>
          <S.CategoryRank>
            {t('product', 'rank.in-category', {
              categoryRank: t('product', 'rank.position', {
                rank,
              }),
              categoryLink: (
                <S.Link
                  to={category.pathName}
                  key="categoryLink"
                  isExternal={renderMode === 'native'}
                >
                  {category.name}
                </S.Link>
              ),
            })}
          </S.CategoryRank>
        </S.Footer>
      ) : null}
    </S.Card>
  );
};
